import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FormEvent, useState } from "react";
import { Button, Identifier, useDataProvider } from "react-admin";

import { useMutation } from "../../../hooks";
import { storage } from "../../../storage";
import {
  AccountResponse,
  CustomDataProvider,
  ModalProps,
} from "../../../types";
import { ActionButtonsBar, Form, Modal } from "..";

interface ExportAccountModalProps extends ModalProps {
  account: AccountResponse;
}

interface SubmitPayload {
  accountId: Identifier;
  exportAccountUsersAndHistoricalData: boolean;
  exportRegions: Record<string, boolean>;
}

function buildDefaultSubmitPayload(
  accountId: Identifier,
  regions: string[],
): SubmitPayload {
  return {
    accountId,
    exportAccountUsersAndHistoricalData: false,
    exportRegions: Object.fromEntries(regions.map((r) => [r, false])),
  };
}

function exportAccount(
  payload: SubmitPayload,
  dataProvider: CustomDataProvider,
): Promise<void[]> {
  const adminUserEmail = storage.loadAuthInfo()?.admin_user.email;
  const promiseFns = [];

  if (payload.exportAccountUsersAndHistoricalData) {
    promiseFns.push(
      dataProvider.exportAccountUsersAndHistoricalData(payload.accountId),
    );
  }

  if (adminUserEmail) {
    Object.entries(payload.exportRegions)
      .filter(([, checked]) => checked)
      .forEach(([region]) => {
        promiseFns.push(
          dataProvider.exportAccountProjectData(
            region,
            payload.accountId,
            adminUserEmail,
          ),
        );
      });
  }

  return Promise.all(promiseFns);
}

function ExportAccountModal(props: ExportAccountModalProps) {
  const regions = storage.enumerateRegions();

  const [submitPayload, setSubmitPayload] = useState<SubmitPayload>(
    buildDefaultSubmitPayload(props.account.id, regions),
  );

  const dataProvider = useDataProvider<CustomDataProvider>();
  const { mutate } = useMutation(
    (payload: SubmitPayload) => exportAccount(payload, dataProvider),
    ["createAccountExport", submitPayload],
    "Account export in progress! ✅",
    true,
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload = submitPayload;
    setSubmitPayload(buildDefaultSubmitPayload(props.account.id, regions));
    props.handleClose();
    mutate(payload);
  };

  const isAnySelected =
    submitPayload.exportAccountUsersAndHistoricalData ||
    Object.values(submitPayload.exportRegions).some(Boolean);

  return (
    <Modal open={props.modalOpen} title="New Account Export">
      <Form onSubmit={handleSubmit}>
        <Typography component="p" sx={{ color: "fieldwire.gray.1" }}>
          Each of the items chosen below will be exported in its own CSV and
          emailed separately to you.
        </Typography>
        <TextField
          label="Account Id"
          name="accountId"
          value={submitPayload.accountId}
          required
          disabled
        />
        <FormControlLabel
          label="Export users and historical usage data"
          control={
            <Checkbox
              name="exportAccountUsersAndHistoricalData"
              onChange={(e) =>
                setSubmitPayload((prev) => ({
                  ...prev,
                  exportAccountUsersAndHistoricalData: e.target.checked,
                }))
              }
              value={submitPayload.exportAccountUsersAndHistoricalData}
            />
          }
        />
        {regions.map((region) => (
          <FormControlLabel
            key={region}
            label={`Export project data from ${region.toUpperCase()}`}
            control={
              <Checkbox
                onChange={(e) =>
                  setSubmitPayload((prev) => ({
                    ...prev,
                    exportRegions: {
                      ...prev.exportRegions,
                      [region]: e.target.checked,
                    },
                  }))
                }
                value={submitPayload.exportRegions[region]}
              />
            }
          />
        ))}
        <ActionButtonsBar>
          <Button
            label="Cancel"
            onClick={props.handleClose}
            variant="fieldwire-secondary"
          />
          <Button
            label="Create Account Export"
            type="submit"
            variant="fieldwire-primary"
            disabled={!isAnySelected}
          />
        </ActionButtonsBar>
      </Form>
    </Modal>
  );
}

export default ExportAccountModal;
